import { ArrowUpRight } from "lucide-react";
import { Line, Reveal, Overline } from "@/components/Kinetic";
import { useLang } from "@/i18n/LanguageContext";

const STEPS_EN = [
  { index: "01", title: "Gather", text: "Credit-card campaigns and offers live scattered across bank apps, emails and messages." },
  { index: "02", title: "Structure", text: "The aim is to bring campaign information into a consistent format." },
  { index: "03", title: "Surface", text: "The proposed experience would help people browse and compare offers." },
  { index: "04", title: "Use", text: "The goal is to help people understand the benefits of cards they already use." },
];

const STEPS_TR = [
  { index: "01", title: "Toplama", text: "Kredi kartı kampanyaları ve fırsatları banka uygulamaları, e-postalar ve mesajlar arasında dağınık durumdadır." },
  { index: "02", title: "Yapılandırma", text: "Amaç, kampanya bilgilerini tutarlı bir biçimde bir araya getirmek." },
  { index: "03", title: "Öne çıkarma", text: "Planlanan deneyim, fırsatları incelemeyi ve karşılaştırmayı kolaylaştıracak." },
  { index: "04", title: "Kullanma", text: "Hedef, kullanıcıların mevcut kartlarının avantajlarını anlamasına yardımcı olmak." },
];

const CONCEPT_ROWS_EN = [
  { tag: "Program", value: "Card campaign" },
  { tag: "Category", value: "Where it applies" },
  { tag: "Validity", value: "When it ends" },
];

const CONCEPT_ROWS_TR = [
  { tag: "Program", value: "Kart kampanyası" },
  { tag: "Kategori", value: "Nerede geçerli" },
  { tag: "Geçerlilik", value: "Ne zaman bitiyor" },
];

export default function KampanyaPage() {
  const { lang, tx } = useLang();
  const steps = lang === "tr" ? STEPS_TR : STEPS_EN;
  const conceptRows = lang === "tr" ? CONCEPT_ROWS_TR : CONCEPT_ROWS_EN;


  return (
    <>
      <section className="bg-paper pt-32 sm:pt-40 lg:pt-44">
        <div className="mx-auto max-w-7xl px-4 pb-20 sm:px-6 sm:pb-28 lg:px-8">
          <div className="grid grid-cols-1 items-start gap-14 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <Reveal immediate y={16}>
                <Overline>{tx("GADULABS — Product initiative", "GADULABS — Ürün girişimi")}</Overline>
              </Reveal>
              <p className="mt-5 inline-flex border border-gold/40 bg-gold/5 px-3 py-2 font-mono text-xs text-ink" data-testid="kampanya-status">{tx("Early development — not yet complete", "Erken geliştirme — henüz tamamlanmadı")}</p>
              <h1
                data-testid="kampanya-hero-title"
                className="mt-7 font-display text-4xl font-bold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl"
              >
                {lang === "tr" ? (
                  <>
                    <Line delay={0.1}>Kredi kartı kampanyaları için</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">daha anlaşılır bir deneyim.</span>
                    </Line>
                  </>
                ) : (
                  <>
                    <Line delay={0.1}>Rethinking</Line>
                    <Line delay={0.22}>
                      <span className="text-slate-400">credit-card campaigns.</span>
                    </Line>
                  </>
                )}
              </h1>
              <Reveal immediate delay={0.45} y={20} className="mt-7 max-w-xl">
                <p className="text-base leading-relaxed text-slate-600 sm:text-lg">
                  {tx(
                    "KampanyamBenim is a GADULABS initiative in early development. Work has only just begun on making credit-card campaigns easier to understand and compare. It is not a finished product.",
                    "KampanyamBenim, GADULABS’ın erken geliştirme aşamasındaki bir girişimidir. Kredi kartı kampanyalarını daha anlaşılır ve karşılaştırılabilir hâle getirme çalışmaları henüz başlangıç düzeyindedir. Tamamlanmış bir ürün değildir.",
                  )}
                </p>
              </Reveal>
              <Reveal immediate delay={0.6} y={20} className="mt-10">
                <a
                  href="mailto:info@gadulabs.com?subject=KampanyamBenim"
                  data-testid="kampanya-hero-cta"
                  className="focus-ring group inline-flex items-center gap-2 bg-ink px-6 py-3.5 text-sm font-medium text-white transition-colors duration-300 hover:bg-navy"
                >
                  {tx("Ask about KampanyamBenim", "KampanyamBenim hakkında bilgi alın")}
                  <ArrowUpRight
                    size={16}
                    className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </a>
              </Reveal>
            </div>
            <Reveal immediate delay={0.5} y={32} className="lg:col-span-5">
              <div
                data-testid="kampanya-concept-panel"
                className="border border-slate-200 bg-white p-6 shadow-[0_24px_60px_-32px_rgba(11,15,23,0.25)] sm:p-8"
              >
                <div className="flex items-center justify-between gap-4">
                  <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    {tx("Structured offer — concept", "Yapılandırılmış fırsat — konsept")}
                  </span>
                  <span className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
                    <span className="h-1.5 w-1.5 bg-gold" aria-hidden="true" />
                    KB
                  </span>
                </div>
                <div className="mt-8 space-y-3.5">
                  {conceptRows.map((r, i) => (
                    <div
                      key={r.tag}
                      data-testid={`kampanya-concept-row-${i + 1}`}
                      className="flex items-center justify-between gap-4 border border-slate-200 bg-paper px-4 py-3.5"
                    >
                      <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-slate-400">
                        {r.tag}
                      </span>
                      <span className="text-right font-display text-sm font-bold tracking-tight text-ink">
                        {r.value}
                      </span>
                    </div>
                  ))}
                </div>
                <p className="mt-6 border-t border-slate-200 pt-4 font-mono text-[10px] uppercase tracking-[0.18em] text-slate-400">
                  {tx(
                    "Many programs — one structured view",
                    "Birçok program — tek yapılandırılmış görünüm",
                  )}
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="border-b border-slate-200 bg-white py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
              <Reveal>
                <Overline>{tx("The problem", "Sorun")}</Overline>
              </Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.08}>
                <h2 className="font-display text-3xl font-bold leading-snug tracking-tight text-ink sm:text-4xl">
                  {tx(
                    "The problem is not a lack of campaigns.",
                    "Sorun kampanya eksikliği değil.",
                  )}
                </h2>
              </Reveal>
              <Reveal delay={0.16}>
                <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
                  {tx(
                    "Card programs run constant offers — but they arrive scattered across apps, inboxes and SMS messages, in different formats, with different conditions. KampanyamBenim explores a structured approach to this problem. The workflow below describes the intended direction, not available features.",
                    "Kart programları sürekli fırsatlar sunar — ancak bunlar uygulamalara, e-posta kutularına ve SMS'lere dağınık, farklı biçimlerde ve farklı koşullarla gelir. KampanyamBenim bu soruna düzenli bir bilgi yapısıyla yaklaşmayı amaçlıyor. Aşağıdaki akış, mevcut özellikleri değil hedeflenen yaklaşımı anlatıyor.",
                  )}
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-paper py-20 sm:py-28 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline>{tx("How it works", "Nasıl çalışır")}</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 max-w-2xl font-display text-3xl font-bold leading-tight tracking-tight text-ink sm:text-4xl">
              {tx("The intended approach.", "Hedeflenen yaklaşım.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 gap-px bg-slate-200 sm:grid-cols-2 lg:grid-cols-4">
            {steps.map((s, i) => (
              <Reveal key={s.index} delay={i * 0.07} y={22} className="bg-paper">
                <div
                  data-testid={`kampanya-step-${s.index}`}
                  className="flex h-full flex-col p-7 transition-colors duration-300 hover:bg-white"
                >
                  <span className="font-mono text-xs tracking-[0.15em] text-gold">
                    {s.index}
                  </span>
                  <h3 className="mt-4 font-display text-base font-bold tracking-tight text-ink">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-slate-600">
                    {s.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-ink py-20 sm:py-28 lg:py-36">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Reveal>
            <Overline dark>GADULABS</Overline>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-8 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-white sm:text-5xl">
              {tx(
                "Help shape the idea.",
                "Fikrin gelişmesine katkıda bulunun.",
              )}
            </h2>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-7 max-w-xl text-base leading-relaxed text-slate-400 sm:text-lg">
              {tx(
                "KampanyamBenim is at an early stage. Share how you currently follow card campaigns and what makes that difficult. No launch date has been announced.",
                "KampanyamBenim henüz erken aşamada. Kart kampanyalarını nasıl takip ettiğinizi ve karşılaştığınız güçlükleri bizimle paylaşabilirsiniz. Açıklanmış bir yayın tarihi bulunmuyor.",
              )}
            </p>
          </Reveal>
          <Reveal delay={0.28} className="mt-10">
            <a
              href="mailto:info@gadulabs.com?subject=KampanyamBenim"
              data-testid="kampanya-final-cta"
              className="focus-ring group inline-flex items-center gap-2 bg-white px-7 py-4 text-sm font-medium text-ink transition-colors duration-300 hover:bg-cobalt hover:text-white"
            >
              {tx("Get in touch", "Bize ulaşın")}
              <ArrowUpRight
                size={16}
                className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
              />
            </a>
          </Reveal>
        </div>
      </section>
    </>
  );
}
